import { useEffect, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { params } from '@aline/routes';
import { useConsumePendingParam } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

/** How long an arrival highlight stays on the row it landed on before fading back out. */
export const ARRIVAL_HIGHLIGHT_DURATION_MS = 7500;

/**
 * The one arrival-highlight treatment, shared by every surface a deep link can land on — the
 * Related Documents buckets and the activity feed — so they cannot drift apart: same ring width,
 * radius, color and fade. Each variant only supplies its own RESTING border — an invisible ring on
 * a bare row, a dashed outline on a card — and all of them reserve that ring up front, so arriving
 * on one changes a color and can never reflow it.
 *
 * Mark the highlighted element with `data-arrival-highlighted="true"`. The highlight lives in an
 * attribute selector rather than a second class because the two variants are composed with classes
 * from other stylesheets, where the winner of a plain class-vs-class collision comes down to
 * injection order.
 */
export const useArrivalHighlightStyles = makeStyles()(theme => {
  const ring = {
    '&[data-arrival-highlighted="true"]': {
      borderColor: theme.palette.primary.main,
      borderStyle: 'solid',
    },
    borderRadius: 8,
    borderWidth: 2,
    transition: theme.transitions.create('border-color', {
      duration: theme.transitions.duration.short,
    }),
  };

  return {
    /** A suggestion card, whose own dashed outline doubles as the ring. */
    cardHighlightTarget: {
      ...ring,
      borderColor: theme.palette.divider,
      borderStyle: 'dashed',
    },
    /**
     * A target that already carries its own inset, e.g. an activity row wrapping a padded card, so
     * the ring adds nothing but the border it reserves — `border-box` keeps that off the width.
     */
    flushHighlightTarget: {
      ...ring,
      borderColor: 'transparent',
      borderStyle: 'solid',
      boxSizing: 'border-box',
    },
    /**
     * A bare row in the confirmed buckets. The negative margin cancels the ring's own padding,
     * keeping row content flush with the rest of the panel.
     */
    rowHighlightTarget: {
      ...ring,
      borderColor: 'transparent',
      borderStyle: 'solid',
      margin: theme.spacing(0, -1),
      padding: theme.spacing(0.75, 1),
      width: 'auto',
    },
  };
});

/**
 * The relationship a deep link asked us to highlight on arrival, held for
 * `ARRIVAL_HIGHLIGHT_DURATION_MS` and then dropped. Arriving again restarts the window, so the
 * previous arrival's timer is cleared rather than left to cut the new highlight short.
 *
 * Acknowledging the arrival is what keeps this to one highlight per deep link. Tab panes live
 * inside `React.Activity`, which preserves state but re-runs effects on every re-show — an
 * unacknowledged arrival would re-highlight (and restart the timer) on every tab switch.
 *
 * The timeout intentionally has no effect cleanup: `React.Activity` tears effects down (and would
 * cancel a cleaned-up timer) whenever a tab is hidden, but it preserves state — so the timer must
 * keep running so the highlight expires on schedule even while the tab is backgrounded.
 */
export function useArrivalHighlightedRelationshipId(): UUID | null {
  const expiryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [highlightedId, setHighlightedId] = useState<UUID | null>(null);
  const [arrival, acknowledgeArrival] = useConsumePendingParam(params.highlightRelationship);

  useEffect(() => {
    if (!arrival) return;

    acknowledgeArrival();
    if (expiryRef.current) clearTimeout(expiryRef.current);
    setHighlightedId(arrival.value);
    expiryRef.current = setTimeout(() => setHighlightedId(null), ARRIVAL_HIGHLIGHT_DURATION_MS);
  }, [acknowledgeArrival, arrival]);

  return highlightedId;
}

/**
 * Ref for a row that should scroll into view the moment it becomes arrival-highlighted (deep
 * linked into from another document's citation).
 *
 * Scrolls once per highlight, not once per effect run. `React.Activity` re-runs effects every time
 * a hidden tab is shown again, and the highlight outlives a quick tab switch — without the latch
 * the panel would yank itself back to this row on the way in, long after the user moved on.
 */
export function useArrivalHighlightScroll(isHighlighted: boolean) {
  const hasScrolledRef = useRef(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isHighlighted) {
      hasScrolledRef.current = false;
      return;
    }
    if (hasScrolledRef.current) return;

    hasScrolledRef.current = true;
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [isHighlighted]);

  return ref;
}
